import { Button, Grid, TextField, makeStyles } from "@material-ui/core";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import { useAuth } from "../../../../../../context/auth";
import { actionShowSpinner } from "../../../../../../redux-flow/action/Wrapper/Spinner";
import {
  IBusinessUnit,
  getAll,
} from "../../../../../../services/business.service";
import { getAll as getAllCommitteePackages } from "../../../../../../services/committePackages.service";
import { Select } from "../../../../../Common/Select";
import SplitButton from "../../../../../Common/SplitButton";
import ToastMessage from "../../../../../Common/ToastMessage";
import { checkAccess } from "../../../../utils";
import BusinessUnitCard from "../../../components/Cards/BusinessUnitCard";
import Header from "../../../components/Header";
import {
  BoxesContainer,
  Container,
  CounterLabel,
  FetchMetaDataContainer,
  FilterButtonsContainer,
  FilterInputs,
  ListCounterContainer,
  ListOrderContainer,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
}));

interface IBusinessUnitProps {
  actionShowSpinner: (value: boolean) => void;
}

const BusinessUnits: React.FC<IBusinessUnitProps> = ({ actionShowSpinner }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const { userInfo, userAccess } = useAuth() as any;

  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [businessUnits, setBusinessUnits] = useState<IBusinessUnit[]>([]);
  const [committeePackages, setCommitteePackages] = useState<
    { id: string; name: string }[]
  >([]);
  const [canCreateBusiness, setCanCreateBusiness] = useState(false);
  const [canCreatePackage, setCanCreatePackage] = useState(false);
  const [canCreateGroup, setCanCreateGroup] = useState(false);
  const [canUpdateGroup, setCanUpdateGroup] = useState(false);
  const [canUpdatePackage, setCanUpdatePackage] = useState(false);
  const [canUpdateCommittee, setCanUpdateCommittee] = useState(false);
  const [canCreateCommittee, setCanCreateCommittee] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    name?: string;
    committeePackage?: { id: string; name: string };
  }>({});

  const fetchBusiness = useCallback(async () => {
    actionShowSpinner(true);
    setLoading(true);

    try {
      const { businessUnits } = await getAll();

      setBusinessUnits(businessUnits);
    } catch (error: any) {
      const [errorMessage] = error?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorFetchBusiness"),
      ];

      setToastMessage({
        open: true,
        content:
          errorMessage ||
          t("softwareManagement.modules.business.errorFetchBusiness"),
        type: "error",
        autoHideDuration: 4000,
      });
    }

    setLoading(false);
    actionShowSpinner(false);
  }, []);

  useEffect(() => {
    fetchCommitteePackages();

    fetchBusiness();
  }, []);

  useEffect(() => {
    if (userInfo?.master) {
      setCanCreateBusiness(true);
    }

    const userCanUpdateGroup = checkAccess(userAccess, "updateGroup");
    const userCanUpdatePackage = checkAccess(
      userAccess,
      "updateCommitteePackage"
    );
    const userCanCreatePackage = checkAccess(
      userAccess,
      "createCommitteePackage"
    );
    const userCanCreateGroup = checkAccess(userAccess, "createGroup");
    const userCanCreateCommittee = checkAccess(userAccess, "createCommitte");
    const userCanUpdateCommittee = checkAccess(userAccess, "updateCommittee");

    setCanCreatePackage(userCanCreatePackage);
    setCanCreateGroup(userCanCreateGroup);
    setCanCreateCommittee(userCanCreateCommittee);
    setCanUpdateGroup(userCanUpdateGroup || userInfo?.master);
    setCanUpdatePackage(userCanUpdatePackage || userInfo?.master);
    setCanUpdateCommittee(userCanUpdateCommittee || userInfo?.master);
  }, [userInfo, userAccess]);

  const handleFilterBusiness = useCallback(async () => {
    actionShowSpinner(true);
    setLoading(true);

    try {
      const { businessUnits } = await getAll({
        ...(filters.name && { name: filters.name }),
        ...(filters.committeePackage && {
          committeePackageId: filters.committeePackage?.id,
        }),
      });

      setBusinessUnits(businessUnits);
    } catch (error: any) {
      const [errorMessage] = error?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorFetchBusiness"),
      ];

      setToastMessage({
        open: true,
        content:
          errorMessage ||
          t("softwareManagement.modules.business.errorFetchBusiness"),
        type: "error",
        autoHideDuration: 4000,
      });
    }

    actionShowSpinner(false);
    setLoading(false);
  }, [filters]);

  const fetchCommitteePackages = useCallback(async () => {
    actionShowSpinner(true);

    try {
      const { committeePackages: committeePackagesOptions } =
        await getAllCommitteePackages();

      setCommitteePackages(committeePackagesOptions);
    } catch (error: any) {
      const [errorMessage] = error?.response?.data?.errors || [
        t("softwareManagement.modules.business.errorFetchCommitteePackages"),
      ];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }

    actionShowSpinner(false);
  }, []);

  const handleCleanFilters = useCallback(async () => {
    actionShowSpinner(true);
    setLoading(true);

    fetchBusiness();
    setFilters({
      name: "",
    });
  }, [filters]);

  return (
    <Container>
      <Header
        title={t("softwareManagement.modules.business.titlePlural")}
        showVectors
        showMenuToggle
        filterInputs={
          <>
            <FilterInputs container direction="column" spacing={2}>
              <Grid style={{ padding: "0 10px 0 10px" }} item lg={6} xs={6}>
                <TextField
                  name="name"
                  id="name"
                  label={t("softwareManagement.name")}
                  value={filters.name}
                  onChange={(e) =>
                    setFilters({ ...filters, name: e.target.value })
                  }
                  fullWidth
                  autoComplete="nope"
                />
              </Grid>

              <Grid style={{ padding: "0 10px 0 10px" }} item lg={6} xs={6}>
                <Select
                  id="committePackage"
                  options={committeePackages}
                  value={filters.committeePackage}
                  setValue={(value: any) =>
                    setFilters({ ...filters, committeePackage: value })
                  }
                  label={t("softwareManagement.modules.committeePackage.title")}
                />
              </Grid>
            </FilterInputs>

            <FilterButtonsContainer>
              <Button variant="outlined" onClick={handleCleanFilters}>
                {t("softwareManagement.clean")}
              </Button>

              <Button
                variant="contained"
                className={classes.button}
                onClick={handleFilterBusiness}
              >
                {t("softwareManagement.search")}
              </Button>
            </FilterButtonsContainer>
          </>
        }
      />

      <FetchMetaDataContainer>
        {loading ? (
          <Stack style={{ width: "100%" }} spacing={1}>
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height={60}
            />
          </Stack>
        ) : (
          <>
            <ListCounterContainer>
              <CounterLabel>
                {businessUnits.length}{" "}
                {t("softwareManagement.modules.business.titlePlural")}
              </CounterLabel>
            </ListCounterContainer>

            <ListOrderContainer>
              <SplitButton
                principalButton={{
                  label: t(
                    "softwareManagement.modules.business.createBusiness"
                  ),
                  disabled: !canCreateBusiness,
                  onClick: () =>
                    history.push("/system-management/configure-business"),
                }}
                options={[
                  {
                    label: t(
                      "softwareManagement.modules.committeePackage.createCommitteePackage"
                    ),
                    disabled: !canCreatePackage,
                    onClick: () =>
                      history.push(
                        "/system-management/configure-committee-packages"
                      ),
                  },
                  {
                    label: t("softwareManagement.modules.groups.createGroups"),
                    disabled: !canCreateGroup,
                    onClick: () =>
                      history.push("/system-management/configure-groups"),
                  },
                  {
                    label: t(
                      "softwareManagement.modules.committee.createCommittee"
                    ),
                    disabled: !canCreateCommittee,
                    onClick: () =>
                      history.push("/system-management/configure-committees"),
                  },
                ]}
              />
            </ListOrderContainer>
          </>
        )}
      </FetchMetaDataContainer>
      <BoxesContainer>
        {loading && (
          <>
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="500px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="500px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="500px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="500px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="500px"
            />
            <Skeleton
              variant={"rectangular"}
              style={{ borderRadius: "8px" }}
              width="100%"
              height="500px"
            />
          </>
        )}
        {businessUnits.map((businessUnit) => (
          <BusinessUnitCard
            canUpdateGroup={canUpdateGroup}
            canUpdatePackage={canUpdatePackage}
            canUpdateCommittee={canUpdateCommittee}
            key={businessUnit.id}
            businessUnit={businessUnit}
          />
        ))}
      </BoxesContainer>
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const BusinessUnitsConnection = connect(
  null,
  mapDispatchToProps
)(BusinessUnits);

export default BusinessUnitsConnection;
